/**
 * Redaction / truncation for Flight Recorder meta + export.
 * Never log Authorization / tokens / PAN in clear.
 */

const SENSITIVE_KEY_RE =
  /^(authorization|auth|token|access[_-]?token|refresh[_-]?token|password|passwd|secret|api[_-]?key|x-api-key|cookie|set-cookie|cvv|cvc|pan|card[_-]?number|pin)$/i

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const DEFAULT_MAX_DEPTH = 6
const DEFAULT_MAX_KEYS = 40
const DEFAULT_MAX_STRING = 240
const DEFAULT_MAX_ARRAY = 20
const DEFAULT_MAX_JSON_CHARS = 12_000

/**
 * @param {string} value
 * @returns {string}
 */
export function maskEmail (value) {
  const s = String(value || '')
  const at = s.indexOf('@')
  if (at <= 0) return '••••'
  const local = s.slice(0, at)
  const domain = s.slice(at + 1)
  const head = local.slice(0, 1) || '•'
  return `${head}${'*'.repeat(Math.min(8, Math.max(1, local.length - 1)))}@${domain}`
}

/**
 * @param {string} value
 * @returns {string}
 */
export function maskBearer (value) {
  const s = String(value || '')
  if (/^Bearer\s+/i.test(s)) return 'Bearer •••••'
  if (s.length > 12) return `${s.slice(0, 4)}…••••`
  return '•••••'
}

/**
 * @param {string} key
 * @returns {boolean}
 */
export function isSensitiveKey (key) {
  return SENSITIVE_KEY_RE.test(String(key || ''))
}

/**
 * @param {unknown} value
 * @param {object} [opts]
 * @param {boolean} [opts.verbose] - include more body depth / keys (still redacts secrets)
 * @param {'keys'|'truncate'|'full'} [opts.bodyMode] - default truncate (keys-only for large objects when not verbose)
 * @returns {unknown}
 */
export function redactValue (value, opts = {}) {
  const verbose = opts.verbose === true
  const bodyMode = opts.bodyMode || (verbose ? 'truncate' : 'keys')
  const maxDepth = verbose ? 8 : DEFAULT_MAX_DEPTH
  const maxKeys = verbose ? 80 : DEFAULT_MAX_KEYS
  const maxString = verbose ? 800 : DEFAULT_MAX_STRING
  const maxArray = verbose ? 40 : DEFAULT_MAX_ARRAY
  const maxJsonChars = verbose ? 24_000 : DEFAULT_MAX_JSON_CHARS

  const seen = new WeakSet()

  function walk (v, depth, keyHint) {
    if (v == null) return v
    if (typeof v === 'string') {
      if (keyHint && isSensitiveKey(keyHint)) return maskBearer(v)
      if (/^Bearer\s+/i.test(v)) return maskBearer(v)
      if (EMAIL_RE.test(v)) return maskEmail(v)
      if (v.length > maxString) return `${v.slice(0, maxString)}…[+${v.length - maxString}]`
      return v
    }
    if (typeof v === 'number' || typeof v === 'boolean') return v
    if (typeof v === 'bigint') return String(v)
    if (typeof v === 'function') return '[Function]'
    if (typeof v !== 'object') return String(v)

    if (seen.has(v)) return '[Circular]'
    seen.add(v)

    if (depth >= maxDepth) {
      if (Array.isArray(v)) return `[Array(${v.length})]`
      return '[Object]'
    }

    if (Array.isArray(v)) {
      const slice = v.slice(0, maxArray).map((item) => walk(item, depth + 1, keyHint))
      if (v.length > maxArray) slice.push(`…[+${v.length - maxArray} items]`)
      return slice
    }

    const keys = Object.keys(v)
    if (bodyMode === 'keys' && depth >= 1 && keys.length > 8 && !verbose) {
      return {
        __keysOnly: true,
        keys: keys.slice(0, maxKeys),
        keyCount: keys.length,
      }
    }

    const out = {}
    const limit = Math.min(keys.length, maxKeys)
    for (let i = 0; i < limit; i++) {
      const k = keys[i]
      if (isSensitiveKey(k)) {
        out[k] = typeof v[k] === 'string' ? maskBearer(v[k]) : '•••••'
        continue
      }
      // Passenger name: partial mask in export-friendly way
      if (/^(name|passengerName|fullName|nombre)$/i.test(k) && typeof v[k] === 'string') {
        const n = String(v[k])
        out[k] = n.length <= 2 ? '••' : `${n.slice(0, 1)}${'•'.repeat(Math.min(10, n.length - 1))}`
        continue
      }
      out[k] = walk(v[k], depth + 1, k)
    }
    if (keys.length > maxKeys) {
      out.__truncated = `+${keys.length - maxKeys} keys`
    }
    return out
  }

  const result = walk(value, 0, null)
  try {
    const raw = JSON.stringify(result)
    if (raw && raw.length > maxJsonChars) {
      return {
        __truncated: true,
        preview: raw.slice(0, maxJsonChars),
        originalChars: raw.length,
      }
    }
  } catch {
    return { __nonSerializable: true }
  }
  return result
}

/**
 * Redact an event (or array of events) for panel/export.
 * @param {object|object[]} input
 * @param {object} [opts]
 */
export function redactFlightEvent (input, opts = {}) {
  if (Array.isArray(input)) {
    return input.map((e) => redactFlightEvent(e, opts))
  }
  if (!input || typeof input !== 'object') return input
  const meta = input.meta != null ? redactValue(input.meta, opts) : undefined
  return { ...input, meta }
}
